import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { Alert, Badge, Center, Group, Loader, SegmentedControl, Stack, Text, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, din277Api, type Din277Entry } from '../../api';
import { DIN277_KINDS } from '../../format';
import { FileDrop } from '../../ui/FileDrop';
import { LibraryTable, type LibraryColumn } from '../../ui/LibraryTable';

type Filter = 'alle' | 'flaechen' | 'regel';

const COLUMNS: LibraryColumn<{ entry: Din277Entry; depth: number }>[] = [
  { key: 'section', title: 'Abschnitt', defaultWidth: 80, render: ({ entry: e }) => <Text size="sm" ff="monospace">{e.section}</Text> },
  {
    key: 'name', title: 'Bezeichnung', defaultWidth: 250, minWidth: 80,
    render: ({ entry: e, depth }) => (
      <Stack gap={2} pl={depth * 14}>
        <Group gap={6} wrap="nowrap">
          {e.abbreviation && <Text size="sm" ff="monospace" fw={700} style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{e.abbreviation}</Text>}
          <Text size="sm" fw={e.kind === 'regel' ? 600 : undefined}>{e.name}</Text>
        </Group>
        {e.kind !== 'regel' && <Badge size="xs" variant="light" color={DIN277_KINDS[e.kind].color}>{DIN277_KINDS[e.kind].label}{e.parent ? ` von ${e.parent}` : ''}</Badge>}
      </Stack>
    ),
  },
  { key: 'text', title: 'Text', defaultWidth: 440, minWidth: 80, render: ({ entry: e }) => <Text size="xs" style={{ whiteSpace: 'pre-line' }}>{e.text}</Text> },
  { key: 'page', title: 'Seite', defaultWidth: 60, align: 'right', render: ({ entry: e }) => <Text size="xs" c="dimmed">{e.page}</Text> },
];

/**
 * The areas in the order of the outline (BGF › NRF › NUF › NUF 1 …), with
 * their depth; entries without abbreviation are left out.
 */
export function outline(entries: readonly Din277Entry[]): { entry: Din277Entry; depth: number }[] {
  const areas = entries.filter((e) => e.abbreviation);
  const children = (parent: string | null) => areas.filter((e) => e.parent === parent);
  const out: { entry: Din277Entry; depth: number }[] = [];
  const seen = new Set<string>();
  const walk = (e: Din277Entry, depth: number): void => {
    if (seen.has(e.abbreviation!)) return;
    seen.add(e.abbreviation!);
    out.push({ entry: e, depth });
    for (const c of children(e.abbreviation)) walk(c, depth + 1);
  };
  for (const root of children(null)) walk(root, 0);
  // Whatever hangs on a parent that is missing stays visible at the end.
  for (const e of areas) if (!seen.has(e.abbreviation!)) walk(e, 0);
  return out;
}

/**
 * Verwaltung › Bibliotheken › DIN 277 (AP-109): terms, the outline of the
 * areas and the rules of measurement from the office's licensed copy
 * (R-24), to look up while measuring areas and volumes for the cost plan.
 */
export function Din277Catalog({ canManage }: { canManage: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: ['din277'], queryFn: din277Api.list });
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('alle');
  const [edition, setEdition] = useState('DIN 277:2021-08');
  const read = useMutation({
    mutationFn: async (file: File) => din277Api.import(await file.text(), edition.trim()),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['din277'] }),
  });
  const all = useMemo(() => list.data ?? [], [list.data]);
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  const match = (e: Din277Entry): boolean => words.every((w) => `${e.section} ${e.abbreviation ?? ''} ${e.name} ${e.text}`.toLowerCase().includes(w));
  const rows = filter === 'flaechen'
    ? outline(all).filter((r) => match(r.entry))
    : all.filter((e) => (filter === 'alle' || e.kind === 'regel') && match(e)).map((entry) => ({ entry, depth: 0 }));

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Begriffe, Gliederung der Grundflächen und Regeln zur Ermittlung von Grundflächen und Rauminhalten nach DIN 277, aus dem lizenzierten Exemplar des Büros. Die Texte sind per Texterkennung aus der PDF gewonnen – im Zweifel gilt die Norm.
      </Text>
      {list.isPending && <Center h={80}><Loader size="sm" /></Center>}
      {list.isError && <Alert color="red">Die Liste konnte nicht geladen werden.</Alert>}
      {list.data && all.length === 0 && <Text size="sm" c="dimmed">Noch nicht eingelesen.</Text>}
      {all.length > 0 && (
        <Group gap="sm">
          <SegmentedControl size="xs" value={filter} onChange={(v) => setFilter(v as Filter)} data={[{ value: 'alle', label: 'Alles' }, { value: 'flaechen', label: 'Flächen und Rauminhalte' }, { value: 'regel', label: 'Regeln' }]} />
          <TextInput size="xs" placeholder="Suchen: Abschnitt, Kurzzeichen, Begriff, Text …" value={query} onChange={(e) => setQuery(e.currentTarget.value)} style={{ flex: 1, minWidth: 200 }} />
          <Text size="xs" c="dimmed">{rows.length} von {all.length} · {all[0]!.edition}</Text>
        </Group>
      )}
      {rows.length > 0 && (
        // Read in as a whole only: an entry is looked at, not changed (AP-128).
        <LibraryTable<{ entry: Din277Entry; depth: number }>
          tableId="din277"
          label="Einträge nach DIN 277"
          columns={COLUMNS}
          rows={rows}
          rowKey={(r) => String(r.entry.position)}
          rowLabel={(r) => [r.entry.section, r.entry.abbreviation, r.entry.name].filter(Boolean).join(' ')}
          rowStyle={({ entry: e }) => (e.kind === 'regel' && !e.text ? { background: 'var(--mantine-color-default-hover)' } : undefined)}
          canManage={canManage}
          fields={({ entry: e }) => [
            { key: 'section', label: 'Abschnitt', value: e.section, mono: true },
            { key: 'abbreviation', label: 'Kurzzeichen', value: e.abbreviation, mono: true },
            { key: 'name', label: 'Bezeichnung', value: e.name },
            { key: 'kind', label: 'Art', value: DIN277_KINDS[e.kind].label },
            { key: 'parent', label: 'Oberbegriff', value: e.parent },
            { key: 'page', label: 'Seite', value: e.page === null ? null : String(e.page) },
            { key: 'edition', label: 'Ausgabe', value: e.edition },
            { key: 'text', label: 'Text', value: e.text, long: true },
          ]}
          maxHeight={620}
          lazyStep={100_000}
        />
      )}
      {canManage && (
        <Stack gap="xs">
          <Text size="sm" fw={500}>Einlesen <Badge size="xs" variant="light" color="gray">Administration</Badge></Text>
          <Text size="xs">CSV mit den Spalten <Text span ff="monospace">abschnitt;kurz;bezeichnung;oberbegriff;text;seite;art</Text> (art: begriff, gliederung oder regel). Ersetzt die ganze Liste.</Text>
          <TextInput size="xs" label="Ausgabe" value={edition} onChange={(e) => setEdition(e.currentTarget.value)} w={220} />
          <FileDrop accept=".csv" label="CSV-Datei hierher ziehen oder auswählen" onFiles={(files) => files[0] && read.mutate(files[0])} disabled={read.isPending || edition.trim().length < 3} />
          {read.isSuccess && <Alert color="green" py={6}>{read.data.entries} Einträge eingelesen.</Alert>}
          {read.isError && <Alert color="red" py={6}>{read.error instanceof ApiError ? read.error.message : 'Einlesen fehlgeschlagen.'}</Alert>}
        </Stack>
      )}
    </Stack>
  );
}
